import type { Editor } from 'tldraw'
import type { StateCreator } from 'zustand'
import { v4 as uuidv4 } from 'uuid'
import { apiService } from '@/service/api'
import { dispatchGraphSaved } from './graphSavedEvent'
import type { GraphData, Property } from '@/types/graph'
import type { authSessionState } from './authSessionSlice'
import type { loadingState } from './loadingSlice'
import type { Node, NodeBase, Edge, SymbolNode } from '@/service/graph'
import { parseGraph, verticesToPolygonWkt } from '@/service/graph'
import { classRecords as defaultClassRecords } from '@/service/classRecords'

/** Parse GraphData into typed Node/Edge Maps for store hydration. */
function buildStateMaps(graph: GraphData): {
  nodeMap: Map<string, Node>
  edgeMap: Map<string, Edge>
} {
  const parsed = parseGraph(graph)
  const droppedEdges = parsed.edges.filter((e) => !e.id)
  if (droppedEdges.length > 0) {
    console.warn(`[buildStateMaps] Dropped ${droppedEdges.length} edge(s) without id`)
  }
  return {
    nodeMap: new Map(parsed.nodes.map((n) => [n.id, n])),
    edgeMap: new Map(
      parsed.edges.filter((e) => e.id).map((e) => [e.id!, e])
    ),
  }
}

export interface Comment {
  id: string
  content: string
  author: string
  createdAt: string
  unread: boolean
}

export class GraphPatch {
  addNodes: Node[] = []
  addEdges: Edge[] = []
  updateNodes: Node[] = []
  updateEdges: Edge[] = []
  deleteNodeIds: string[] = []
  deleteEdgeIds: string[] = []

  constructor(
    initialNodes: Map<string, Node>,
    initialEdges: Map<string, Edge>,
    currentNodes: Map<string, Node>,
    currentEdges: Map<string, Edge>
  ) {
    currentNodes.forEach((node, id) => {
      const initialNode = initialNodes.get(id)
      if (!initialNode) {
        this.addNodes.push(node)
      } else if (JSON.stringify(initialNode) !== JSON.stringify(node)) {
        this.updateNodes.push(node)
      }
    })

    initialNodes.forEach((_, id) => {
      if (!currentNodes.has(id)) {
        this.deleteNodeIds.push(id)
      }
    })

    currentEdges.forEach((edge, id) => {
      const initialEdge = initialEdges.get(id)
      if (!initialEdge) {
        this.addEdges.push(edge)
      } else if (JSON.stringify(initialEdge) !== JSON.stringify(edge)) {
        this.updateEdges.push(edge)
      }
    })

    initialEdges.forEach((_, id) => {
      if (!currentEdges.has(id)) {
        this.deleteEdgeIds.push(id)
      }
    })
  }

  isModified(): boolean {
    return (
      [
        ...this.addNodes,
        ...this.addEdges,
        ...this.updateNodes,
        ...this.updateEdges,
        ...this.deleteNodeIds,
        ...this.deleteEdgeIds,
      ].length > 0
    )
  }
}

/** What the parse calls a link between a line and a symbol; the KG and Graph Preview read lines by it. */
const TOUCHES_CONNECTION = 'touches_connection'

/** A link drawn between a line and a symbol touches it, the relation the parse gives the same link. */
function withLinkRelation(edge: Edge, nodes: Map<string, Node>): Edge {
  if (edge.relation) return edge
  const kinds = [nodes.get(edge.fromNodeId)?.type, nodes.get(edge.toNodeId)?.type]
  return kinds.includes('connection') && kinds.includes('symbol')
    ? { ...edge, relation: TOUCHES_CONNECTION }
    : edge
}

/** Word-style annotation undo/redo keeps at most this many steps each way. */
const UNDO_HISTORY_DEPTH = 5

/** Shallow snapshot of the editable graph (currentState). Cheap and correct
 *  because every graphSlice mutation rebuilds these Maps immutably, so the
 *  captured node/edge objects are never mutated in place afterwards. */
type GraphSnapshot = { nodes: Map<string, Node>; edges: Map<string, Edge> }

const snapshotOf = (s: { nodes: Map<string, Node>; edges: Map<string, Edge> }): GraphSnapshot => ({
  nodes: new Map(s.nodes),
  edges: new Map(s.edges),
})

export interface GraphState {
  graph: GraphData
  graphId: string
  showChat: boolean
  showBackground: boolean
  showEdges: boolean
  graphLocked: boolean
  classRecords: Record<
    string,
    { color: string; colorName: string; label: string; category: string }
  >
  showEditModal: boolean
  selectedClass: string
  showClassEditor: boolean
  showFilterPanel: boolean
  initialState: {
    nodes: Map<string, Node>
    edges: Map<string, Edge>
    isComplete: boolean
  }
  currentState: {
    nodes: Map<string, Node>
    edges: Map<string, Edge>
    isModified: boolean
  }
  /** Bounded annotation undo/redo history. `undoPast` holds prior currentState
   *  snapshots (oldest→newest) to step back to; `undoFuture` holds snapshots
   *  redo steps forward to. Each capped at UNDO_HISTORY_DEPTH. */
  undoPast: GraphSnapshot[]
  undoFuture: GraphSnapshot[]
  comments: Comment[]
  versionHistory: {
    versions: Array<{
      version: number
      created_at: string
      user_id: string
    }>
    currentVersion: number | null
  }

  /** Resets UI flags and clears node/edge Maps. Does NOT parse graph data —
   *  call hydrateState afterwards (or let the Web Worker → hydrateState path
   *  populate the Maps). */
  resetState: (graph: GraphData, graphId: string) => void
  /** Populates initialState and currentState from pre-parsed Node/Edge
   *  arrays.  Sets isComplete: true so subsequent addNode calls only
   *  modify currentState.  Use this instead of addNodes+markComplete. */
  hydrateState: (
    nodes: Node[],
    edges: Edge[],
    graph: GraphData,
    graphId: string,
  ) => void
  unregisterLockHandler: () => void
  setShowChat: (showChat: boolean) => void
  setShowBackground: (showBackground: boolean) => void
  setShowEdges: (showEdges: boolean) => void
  setGraphLocked: (graphLocked: boolean, editor: Editor) => void
  setClassRecords: (
    classRecords: Record<
      string,
      { color: string; colorName: string; label: string; category: string }
    >
  ) => void
  setSelectedClass: (selectedClass: string) => void
  setUnregisterLockHandler: (unregisterLockHandler: () => void) => void
  setShowClassEditor: (showClassEditor: boolean) => void
  setShowFilterPanel: (showFilterPanel: boolean) => void
  setShowEditModal: (isOpen: boolean) => void

  addNode: (node: Node) => Node
  addNodes: (nodes: Node[]) => void
  addEdge: (edge: Edge) => Edge
  addEdges: (edges: Edge[]) => void
  updateNode: (node: Node) => Node
  /**
   * ENG-2624: apply the same partial patch to many nodes in one store commit
   * (batch re-classify). Scoped to NodeBase minus id/type so a patch can't corrupt
   * a node's map key or discriminated-union tag. Missing ids are skipped; edges
   * untouched. Caller MUST snapshot (recordHistory / recordCanvasSnapshot) first to
   * make the batch a single Undo step — same contract as updateNode / deleteMultipleNodes.
   * ENG-2632: `content` (tag-node text, not on NodeBase) is allowed too so a batch
   * fill/clear of the "Tag" field can reach tag nodes.
   * Likewise `row`/`col`/`table_index`, so inserting or deleting a table row shifts a whole
   * band of cells in one commit rather than one write per cell.
   */
  updateMultipleNodes: (
    nodeIds: string[],
    patch: Partial<Omit<NodeBase, 'id' | 'type'>> & {
      content?: string
      row?: number
      col?: number
      table_index?: number
      col_span?: number
    },
  ) => void
  deleteNodeById: (nodeId: string) => void
  deleteMultipleNodes: (nodeIds: string[]) => void
  deleteEdgeById: (edgeId: string) => void
  /** Snapshot currentState onto the undo stack before an annotation action,
   *  dropping the redo stack. Caps the stack at UNDO_HISTORY_DEPTH. */
  recordHistory: () => void
  /** Step back to the previous snapshot (no-op when there's nothing to undo). */
  undo: () => void
  /** Step forward to the next snapshot (no-op when there's nothing to redo). */
  redo: () => void
  getNodeById: (nodeId: string) => Node
  markGraphInitialStateAsComplete: () => void
  publishGraph: () => Promise<void>
  fetchComments: (graph_id: string) => void
  addComment: (content: string) => void

  setVersionHistory: (
    versions: Array<{ version: number; created_at: string; user_id: string }>
  ) => void
  setCurrentVersion: (version: number | null) => void
  loadVersion: (version: number) => Promise<void>
}

export const graphSlice: StateCreator<
  GraphState & loadingState & authSessionState,
  [],
  [],
  GraphState
> = (set, get) => ({
  graph: { id: '', nodes: new Set(), edges: {}, properties: {} },
  graphId: '',
  showChat: false,
  showBackground: true,
  showEdges: true,
  graphLocked: false,
  selectedClass: '',
  classRecords: defaultClassRecords,
  showEditModal: false,
  showClassEditor: false,
  showFilterPanel: false,
  initialState: { nodes: new Map(), edges: new Map(), isComplete: false },
  currentState: { nodes: new Map(), edges: new Map(), isModified: false },
  undoPast: [],
  undoFuture: [],
  comments: [],
  versionHistory: {
    versions: [],
    currentVersion: null,
  },

  resetState: (graph, graphId) => {
    set({
      graph,
      graphId,
      showBackground: true,
      showEdges: true,
      graphLocked: false,
      selectedClass: '',
      classRecords: defaultClassRecords,
      showEditModal: false,
      showClassEditor: false,
      showFilterPanel: false,
      initialState: { nodes: new Map(), edges: new Map(), isComplete: false },
      currentState: { nodes: new Map(), edges: new Map(), isModified: false },
      undoPast: [],
      undoFuture: [],
    })
  },

  hydrateState: (nodes, edges, graph, graphId) => {
    const nodeMap = new Map<string, Node>(nodes.map((n) => [n.id, n]))
    const edgeMap = new Map<string, Edge>(
      edges.filter((e) => e.id).map((e) => [e.id!, e])
    )
    set({
      graph,
      graphId,
      showBackground: true,
      showEdges: true,
      graphLocked: false,
      selectedClass: '',
      classRecords: defaultClassRecords,
      showEditModal: false,
      showClassEditor: false,
      showFilterPanel: false,
      initialState: { nodes: new Map(nodeMap), edges: new Map(edgeMap), isComplete: true },
      currentState: { nodes: new Map(nodeMap), edges: new Map(edgeMap), isModified: false },
      undoPast: [],
      undoFuture: [],
    })
  },

  unregisterLockHandler: () => {},
  setUnregisterLockHandler: (unregisterLockHandler) =>
    set({ unregisterLockHandler }),
  setShowChat: (showChat) => set({ showChat }),
  setShowBackground: (showBackground) => set({ showBackground }),
  setShowEdges: (showEdges) => set({ showEdges }),
  // TODO: Remove methos as is deprecated per TLDraw
  setGraphLocked: (graphLocked, editor) => {
    set({ graphLocked })
    if (graphLocked) {
      const unregister = editor.sideEffects.registerBeforeChangeHandler(
        'shape',
        (prev, next) => (prev.opacity === next.opacity ? prev : next)
      )
      set({ unregisterLockHandler: unregister })
    } else {
      const unregister = get().unregisterLockHandler
      if (unregister) unregister()
      set({ unregisterLockHandler: undefined })
    }
  },
  setClassRecords: (classRecords) => set({ classRecords }),
  setSelectedClass: (selectedClass) => set({ selectedClass }),
  // TODO: Remove methos as is deprecated per TLDraw
  setShowClassEditor: (showClassEditor) => set({ showClassEditor }),
  // TODO: Remove methos as is deprecated per TLDraw
  setShowFilterPanel: (showFilterPanel) => set({ showFilterPanel }),
  // TODO: Remove methos as is deprecated per TLDraw
  setShowEditModal: (isOpen) => set({ showEditModal: isOpen }),

  addNode: (node) => {
    const nodeId = node.id ?? uuidv4()
    set((state) => {
      const initialNodes = state.initialState.isComplete
        ? state.initialState.nodes
        : new Map(state.initialState.nodes).set(nodeId, { ...node, id: nodeId })
      const currentNodes = new Map(state.currentState.nodes).set(nodeId, {
        ...node,
        id: nodeId,
      })
      return {
        initialState: { ...state.initialState, nodes: initialNodes },
        currentState: {
          ...state.currentState,
          nodes: currentNodes,
          isModified: new GraphPatch(
            initialNodes,
            state.initialState.edges,
            currentNodes,
            state.currentState.edges
          ).isModified(),
        },
      }
    })
    return { ...node, id: nodeId }
  },

  addNodes: (nodes) => {
    set((state) => {
      const initialNodes = new Map(state.initialState.nodes)
      const currentNodes = new Map(state.currentState.nodes)
      for (const node of nodes) {
        const nodeId = node.id ?? uuidv4()
        if (!state.initialState.isComplete)
          initialNodes.set(nodeId, { ...node, id: nodeId })
        currentNodes.set(nodeId, { ...node, id: nodeId })
      }
      const isModified = new GraphPatch(
        initialNodes,
        state.initialState.edges,
        currentNodes,
        state.currentState.edges
      ).isModified()
      return {
        initialState: { ...state.initialState, nodes: initialNodes },
        currentState: {
          ...state.currentState,
          nodes: currentNodes,
          isModified,
        },
      }
    })
  },

  addEdge: (edge) => {
    const added = { ...withLinkRelation(edge, get().currentState.nodes), id: edge.id ?? uuidv4() }
    set((state) => {
      const initialEdges = state.initialState.isComplete
        ? state.initialState.edges
        : new Map(state.initialState.edges).set(added.id, added)
      const currentEdges = new Map(state.currentState.edges).set(added.id, added)
      return {
        initialState: { ...state.initialState, edges: initialEdges },
        currentState: {
          ...state.currentState,
          edges: currentEdges,
          isModified: new GraphPatch(
            state.initialState.nodes,
            initialEdges,
            state.currentState.nodes,
            currentEdges
          ).isModified(),
        },
      }
    })
    return added
  },

  addEdges: (edges) => {
    set((state) => {
      const initialEdges = new Map(state.initialState.edges)
      const currentEdges = new Map(state.currentState.edges)
      for (const edge of edges) {
        const added = { ...withLinkRelation(edge, state.currentState.nodes), id: edge.id ?? uuidv4() }
        if (!state.initialState.isComplete) initialEdges.set(added.id, added)
        currentEdges.set(added.id, added)
      }
      const isModified = new GraphPatch(
        state.initialState.nodes,
        initialEdges,
        state.currentState.nodes,
        currentEdges
      ).isModified()
      return {
        initialState: { ...state.initialState, edges: initialEdges },
        currentState: {
          ...state.currentState,
          edges: currentEdges,
          isModified,
        },
      }
    })
  },

  updateNode: (node) => {
    const nodeId = node.id!
    set((state) => {
      const nodes = new Map(state.currentState.nodes)
      nodes.set(nodeId, node)
      const isModified = new GraphPatch(
        state.initialState.nodes,
        state.initialState.edges,
        nodes,
        state.currentState.edges
      ).isModified()
      return { currentState: { ...state.currentState, nodes, isModified } }
    })
    return node
  },

  // ENG-2624: batch-apply a partial patch (e.g. pnid_level_i/ii/iii from the
  // bulk re-classify bar) to every listed node, in a single store commit so it's
  // one Undo step and one re-render. Missing ids are skipped.
  updateMultipleNodes: (nodeIds, patch) =>
    set((state) => {
      const nodes = new Map(state.currentState.nodes)
      for (const nodeId of nodeIds) {
        const existing = nodes.get(nodeId)
        if (!existing) continue
        nodes.set(nodeId, { ...existing, ...patch } as Node)
      }
      const isModified = new GraphPatch(
        state.initialState.nodes,
        state.initialState.edges,
        nodes,
        state.currentState.edges
      ).isModified()
      return { currentState: { ...state.currentState, nodes, isModified } }
    }),

  deleteNodeById: (nodeId) =>
    set((state) => {
      const nodes = new Map(state.currentState.nodes)
      nodes.delete(nodeId)
      const isModified = new GraphPatch(
        state.initialState.nodes,
        state.initialState.edges,
        nodes,
        state.currentState.edges
      ).isModified()
      return { currentState: { ...state.currentState, nodes, isModified } }
    }),

  deleteMultipleNodes: (nodeIds) =>
    set((state) => {
      const nodes = new Map(state.currentState.nodes)
      const edges = new Map(state.currentState.edges)
      
      // Delete nodes and their associated edges
      nodeIds.forEach((nodeId) => {
        nodes.delete(nodeId)
        // Find and delete all edges connected to this node
        const edgesToDelete: string[] = []
        edges.forEach((edge, edgeId) => {
          if (edge.fromNodeId === nodeId || edge.toNodeId === nodeId) {
            edgesToDelete.push(edgeId)
          }
        })
        edgesToDelete.forEach((edgeId) => edges.delete(edgeId))
      })

      const isModified = new GraphPatch(
        state.initialState.nodes,
        state.initialState.edges,
        nodes,
        edges
      ).isModified()
      return { currentState: { ...state.currentState, nodes, edges, isModified } }
    }),

  deleteEdgeById: (edgeId) =>
    set((state) => {
      const edges = new Map(state.currentState.edges)
      edges.delete(edgeId)
      const isModified = new GraphPatch(
        state.initialState.nodes,
        state.initialState.edges,
        state.currentState.nodes,
        edges
      ).isModified()
      return { currentState: { ...state.currentState, edges, isModified } }
    }),

  recordHistory: () =>
    set((state) => ({
      undoPast: [...state.undoPast, snapshotOf(state.currentState)].slice(-UNDO_HISTORY_DEPTH),
      undoFuture: [],
    })),

  undo: () =>
    set((state) => {
      if (state.undoPast.length === 0) return {}
      const undoPast = state.undoPast.slice()
      const snapshot = undoPast.pop()!
      const undoFuture = [snapshotOf(state.currentState), ...state.undoFuture].slice(0, UNDO_HISTORY_DEPTH)
      const isModified = new GraphPatch(
        state.initialState.nodes,
        state.initialState.edges,
        snapshot.nodes,
        snapshot.edges
      ).isModified()
      return {
        undoPast,
        undoFuture,
        currentState: { ...state.currentState, nodes: snapshot.nodes, edges: snapshot.edges, isModified },
      }
    }),

  redo: () =>
    set((state) => {
      if (state.undoFuture.length === 0) return {}
      const undoFuture = state.undoFuture.slice()
      const snapshot = undoFuture.shift()!
      const undoPast = [...state.undoPast, snapshotOf(state.currentState)].slice(-UNDO_HISTORY_DEPTH)
      const isModified = new GraphPatch(
        state.initialState.nodes,
        state.initialState.edges,
        snapshot.nodes,
        snapshot.edges
      ).isModified()
      return {
        undoPast,
        undoFuture,
        currentState: { ...state.currentState, nodes: snapshot.nodes, edges: snapshot.edges, isModified },
      }
    }),

  getNodeById: (nodeId) => get().currentState.nodes.get(nodeId)!,

  markGraphInitialStateAsComplete: () =>
    set({ initialState: { ...get().initialState, isComplete: true } }),

  publishGraph: async () => {
    const { initialState, currentState, graphId } = get()

    if (!graphId) {
      throw new Error('Cannot save: graph is still loading. Please wait and try again.')
    }

    const patch = new GraphPatch(
      initialState.nodes,
      initialState.edges,
      currentState.nodes,
      currentState.edges
    )

    const properties_to_upsert: Property[] = []

    const get_geometry_wkt = (node: Node) => {
      // Freeform polygon (ENG-2582): serialize the traced outline as a closed
      // ring so the exact vertices persist to the JSON, not just a bbox.
      if (node.vertices && node.vertices.length >= 3) {
        return verticesToPolygonWkt(node.vertices)
      }

      const x = node.x
      const y = node.y
      const w = node.width
      const h = node.height

      // Only create geometry_wkt if all position properties exist
      if (
        x === undefined ||
        y === undefined ||
        w === undefined ||
        h === undefined
      ) {
        return null
      }

      return `POLYGON ((${x} ${y}, ${x + w} ${y}, ${x + w} ${y + h}, ${x} ${
        y + h
      }, ${x} ${y}))`
    }

    // Collect all nodes that need their properties sent to the backend
    // This includes newly added nodes, updated nodes, AND nodes involved in edge changes
    const nodesInvolvedInEdges = new Set<string>()

    // Add nodes from added/updated edges to ensure their geometry is preserved
    for (const edge of [...patch.addEdges, ...patch.updateEdges]) {
      nodesInvolvedInEdges.add(edge.fromNodeId)
      nodesInvolvedInEdges.add(edge.toNodeId)
    }

    // Get the full node objects for nodes involved in edges
    const nodesFromEdges: Node[] = []
    nodesInvolvedInEdges.forEach((nodeId) => {
      const node = currentState.nodes.get(nodeId)
      if (node && !patch.addNodes.some(n => n.id === nodeId) && !patch.updateNodes.some(n => n.id === nodeId)) {
        nodesFromEdges.push(node)
      }
    })

    const all_updated_nodes = [...patch.addNodes, ...patch.updateNodes, ...nodesFromEdges]

    // Handle properties for new nodes
    const duplicatePropertyIds: string[] = []
    for (const node of all_updated_nodes) {
      const nodeId = node.id

      const existingProps = Object.values(get().graph.properties).filter(
        (p) => p.target_id === nodeId
      )
      // Return the first matching property ID and mark any duplicates for deletion.
      // Duplicates were created by a prior bug that used uuidv4() instead of
      // reusing the existing property ID on every save.
      const getExistingPropId = (key: string) => {
        const matches = existingProps.filter((p) => p.key === key)
        if (matches.length > 1) {
          duplicatePropertyIds.push(...matches.slice(1).map(p => p.id))
        }
        return matches[0]?.id
      }

      properties_to_upsert.push({
        id: getExistingPropId('type') || uuidv4(),
        target_id: nodeId,
        key: 'type',
        value: node.type,
        is_node_property: true,
      })

      // Persist 3-level PIC001 classification when Level I and II are set.
      // Level III is always written alongside Level II (even as empty string) so
      // that clearing a previously-set Level III actually removes the stale value
      // from the backend instead of leaving it orphaned.
      if (node.pnid_level_i) {
        properties_to_upsert.push({
          id: getExistingPropId('pnid_level_i') || uuidv4(),
          target_id: nodeId,
          key: 'pnid_level_i',
          value: node.pnid_level_i,
          is_node_property: true,
        })
      }
      if (node.pnid_level_ii) {
        properties_to_upsert.push({
          id: getExistingPropId('pnid_level_ii') || uuidv4(),
          target_id: nodeId,
          key: 'pnid_level_ii',
          value: node.pnid_level_ii,
          is_node_property: true,
        })
        properties_to_upsert.push({
          id: getExistingPropId('pnid_level_iii') || uuidv4(),
          target_id: nodeId,
          key: 'pnid_level_iii',
          value: node.pnid_level_iii ?? '',
          is_node_property: true,
        })
      }

      // Persist geometry: use LINESTRING/MULTILINESTRING for connections, else POLYGON
      const geometryWkt =
        node.type === 'connection'
          ? node.geometry_wkt ?? null
          : get_geometry_wkt(node)
      if (geometryWkt !== null) {
        properties_to_upsert.push({
          id: getExistingPropId('geometry_wkt') || uuidv4(),
          target_id: nodeId,
          key: 'geometry_wkt',
          value: geometryWkt,
          is_node_property: true,
        })
      }

      if (node.confidence !== undefined) {
        properties_to_upsert.push({
          id: getExistingPropId('confidence') || uuidv4(),
          target_id: nodeId,
          key: 'confidence',
          value: node.confidence.toString(),
          is_node_property: true,
        })
      }

      // annotation_text (ENG-2595) is a NodeBase field exposed on the Node Details
      // panel for EVERY node type, so persist it type-agnostically here rather than
      // per-branch — otherwise tag/table_cell edits silently drop it on save.
      if (node.annotation_text) {
        properties_to_upsert.push({
          id: getExistingPropId('annotation_text') || uuidv4(),
          target_id: nodeId,
          key: 'annotation_text',
          value: node.annotation_text,
          is_node_property: true,
        })
      }

      // VISN-319: persist the human-provenance flags (ENG-2622). Both are
      // KNOWN_PROPERTY_KEYS, so they never round-trip via extraProperties —
      // without these upserts an Approve click or a manually-drawn object
      // silently lost its provenance on the next reload, and ground truth
      // cannot distinguish human-verified objects from untouched detections.
      if (node.approved === true) {
        properties_to_upsert.push({
          id: getExistingPropId('approved') || uuidv4(),
          target_id: nodeId,
          key: 'approved',
          value: 'true',
          is_node_property: true,
        })
      }
      // `source` only for manually-drawn objects: absent means detected (the
      // reader's invariant), so writing 'detected' would just accumulate
      // redundant rows on every re-save.
      if (node.source === 'manual') {
        properties_to_upsert.push({
          id: getExistingPropId('source') || uuidv4(),
          target_id: nodeId,
          key: 'source',
          value: node.source,
          is_node_property: true,
        })
      }

      if (node.type === 'symbol') {
        const symbolNode = node as SymbolNode
        properties_to_upsert.push({
          id: getExistingPropId('name') || uuidv4(),
          target_id: nodeId,
          key: 'name',
          value: symbolNode.name,
          is_node_property: true,
        })
        // Round-trip the symbol's category. The generator buckets symbols off
        // `category` (e.g. a manually-placed junction is category="junction"), so
        // without persisting it a regen reads the node back as "unknown". Fire when
        // `category` is set AND differs from the round-tripped `extraProperties.category`:
        // that covers a FRESH add (no extraProperties yet) AND a human RECLASSIFY of a
        // loaded symbol (the panel writes the new `category`, extraProperties still holds
        // the old one). The old `!extraProperties?.category` gate silently dropped every
        // reclassify of a detected symbol — the extraProperties loop below then re-emitted
        // the STALE value, so the KG never saw the human's new class (audit #2). The loop
        // now yields to already-upserted keys, so this fresh value wins.
        if (symbolNode.category !== undefined && symbolNode.category !== symbolNode.extraProperties?.category) {
          properties_to_upsert.push({
            id: getExistingPropId('category') || uuidv4(),
            target_id: nodeId,
            key: 'category',
            value: symbolNode.category,
            is_node_property: true,
          })
        }
        // Round-trip a manually-set symbol_type (fine subtype → symbols[].type → db_ask
        // dexpi_class). Same shape/gate as category — fire when it differs from the
        // round-tripped value so a reclassify persists (not just a fresh add).
        const symbolType = symbolNode.symbol_type
        // `!== undefined` (not truthy) so an explicit clear (`''`) is persisted too —
        // e.g. reclassifying a symbol to Annotation clears the stale fine subtype rather
        // than leaving it to contradict the new class (mirrors the category clear above).
        if (symbolType !== undefined && symbolType !== symbolNode.extraProperties?.symbol_type) {
          properties_to_upsert.push({
            id: getExistingPropId('symbol_type') || uuidv4(),
            target_id: nodeId,
            key: 'symbol_type',
            value: symbolType,
            is_node_property: true,
          })
        }
        if (symbolNode.label) {
          properties_to_upsert.push({
            id: getExistingPropId('label') || uuidv4(),
            target_id: nodeId,
            key: 'label',
            value: symbolNode.label,
            is_node_property: true,
          })
        }
        if (symbolNode.note) {
          properties_to_upsert.push({
            id: getExistingPropId('note') || uuidv4(),
            target_id: nodeId,
            key: 'note',
            value: symbolNode.note,
            is_node_property: true,
          })
        }
        // Add instrument properties if this symbol is an instrument
        if (symbolNode.is_instrument) {
          properties_to_upsert.push({
            id: getExistingPropId('is_instrument') || uuidv4(),
            target_id: nodeId,
            key: 'is_instrument',
            value: 'true',
            is_node_property: true,
          })
        }
      } else if (node.type === 'tag') {
        properties_to_upsert.push({
          id: getExistingPropId('content') || uuidv4(),
          target_id: nodeId,
          key: 'content',
          value: node.content || '',  // Allow empty content for newly created tags
          is_node_property: true,
        })
        if (node.note) {
          properties_to_upsert.push({
            id: getExistingPropId('note') || uuidv4(),
            target_id: nodeId,
            key: 'note',
            value: node.note,
            is_node_property: true,
          })
        }
      } else if (node.type === 'connection') {
        if (node.label) {
          properties_to_upsert.push({
            id: getExistingPropId('label') || uuidv4(),
            target_id: nodeId,
            key: 'label',
            value: node.label,
            is_node_property: true,
          })
        }
        if (node.note) {
          properties_to_upsert.push({
            id: getExistingPropId('note') || uuidv4(),
            target_id: nodeId,
            key: 'note',
            value: node.note,
            is_node_property: true,
          })
        }
      } else if (node.type === 'table_cell') {
        // A cell's value is `content`; its place is `row`/`col`/`table_index`. All four are
        // KNOWN_PROPERTY_KEYS, so they never reach `extraProperties` either — without these
        // upserts a grid edit was dropped on save and a new cell lost its coordinates.
        properties_to_upsert.push({
          id: getExistingPropId('content') || uuidv4(),
          target_id: nodeId,
          key: 'content',
          value: node.content ?? '',  // '' is a real value: a blank cell in a table
          is_node_property: true,
        })
        properties_to_upsert.push({
          id: getExistingPropId('name') || uuidv4(),
          target_id: nodeId,
          key: 'name',
          value: node.name || node.content || '',
          is_node_property: true,
        })
        // Gate on `!== undefined`, not truthiness — row/col/table 0 are all valid.
        for (const [key, value] of [
          ['row', node.row],
          ['col', node.col],
          ['table_index', node.table_index],
          // Only when it actually spans; 1 is the default and stays absent.
          ['col_span', node.col_span && node.col_span > 1 ? node.col_span : undefined],
        ] as const) {
          if (value === undefined) continue
          properties_to_upsert.push({
            id: getExistingPropId(key) || uuidv4(),
            target_id: nodeId,
            key,
            value: String(value),
            is_node_property: true,
          })
        }
        if (node.category) {
          properties_to_upsert.push({
            id: getExistingPropId('category') || uuidv4(),
            target_id: nodeId,
            key: 'category',
            value: node.category,
            is_node_property: true,
          })
        }
        if (node.label) {
          properties_to_upsert.push({
            id: getExistingPropId('label') || uuidv4(),
            target_id: nodeId,
            key: 'label',
            value: node.label,
            is_node_property: true,
          })
        }
        if (node.note) {
          properties_to_upsert.push({
            id: getExistingPropId('note') || uuidv4(),
            target_id: nodeId,
            key: 'note',
            value: node.note,
            is_node_property: true,
          })
        }
      }

      // Persist extra properties (e.g. connected_to_* from cross-sheet linking)
      if (node.extraProperties) {
        // A top-level field pushed above (e.g. a reclassify writing `category`/
        // `symbol_type`) is the human's FRESH value and MUST win over the stale
        // round-tripped extraProperties entry — skip any key already upserted for
        // this node so the loop doesn't overwrite it with the old value (audit #2).
        const alreadyUpserted = new Set(
          properties_to_upsert.filter((p) => p.target_id === nodeId).map((p) => p.key),
        )
        for (const [key, value] of Object.entries(node.extraProperties)) {
          if (alreadyUpserted.has(key)) continue
          properties_to_upsert.push({
            id: getExistingPropId(key) || uuidv4(),
            target_id: nodeId,
            key,
            value,
            is_node_property: true,
          })
        }
      }

      // A user-editable text field emptied this save (Tag→label, Note→note,
      // Annotation Text→annotation_text) is not re-upserted above. Force-upsert it as
      // '' (in place, reusing the row id) rather than DELETING the row — audit #7: a
      // deleted row leaves `getCurrentNodeState` blank, so `buildOverrides` skips the
      // key and the base/vision value merges back on regen/reproject; an explicit ''
      // is recorded as a real clear-override. (Mirrors the tag `content` force-'' path;
      // the old value still can't reload since the stored value is now ''.)
      // Skip a key still present in the upserts (incl. via extraProperties) — real value.
      const upsertedKeysForNode = new Set(
        properties_to_upsert.filter((p) => p.target_id === nodeId).map((p) => p.key),
      )
      // `col_span` joins these: it is only upserted when > 1, so a merged cell later split
      // back to one column would keep its stale span on reload.
      // The pnid levels join these too: they are only upserted when level_i is truthy, so
      // clearing a cell's classification (a header row that became a body row) left the old
      // value in place and it came back on reload.
      for (const clearableKey of [
        'label', 'note', 'annotation_text', 'col_span',
        'pnid_level_i', 'pnid_level_ii', 'pnid_level_iii',
      ] as const) {
        if (upsertedKeysForNode.has(clearableKey)) continue
        for (const p of existingProps) {
          if (p.key === clearableKey) {
            properties_to_upsert.push({ id: p.id, target_id: nodeId, key: clearableKey, value: '', is_node_property: true })
          }
        }
      }
    }

    // Latest is versions[0] (the history endpoint returns newest-first, and
    // publishGraph itself reads history[0] as the latest below).
    const latestKnownVersion = get().versionHistory.versions[0]?.version
    const viewedVersion = get().versionHistory.currentVersion
    const isViewingOlderVersion =
      latestKnownVersion !== undefined &&
      viewedVersion !== undefined &&
      viewedVersion !== null &&
      viewedVersion < latestKnownVersion

    // The client still HAS a graph. This is what separates a person deleting 400 of
    // 800 objects (400 remain on screen) from a client that lost its state and is
    // about to diff an empty map into "delete the whole drawing" (nothing remains).
    // Both produce a bare mass delete on the wire and the backend cannot tell them
    // apart; only the client knows, so only the client can say.
    const clientHasGraphState = get().currentState.nodes.size > 0

    // A new link's relation is stored on the link; without it the KG cannot tell what it joins.
    for (const edge of patch.addEdges) {
      if (!edge.relation) continue
      properties_to_upsert.push({
        id: uuidv4(),
        target_id: edge.id!,
        key: 'relation',
        value: edge.relation,
        is_node_property: false,
      })
    }

    const graphPatch = {
      nodes_to_add: patch.addNodes.map((node) => node.id),
      nodes_to_delete: patch.deleteNodeIds,
      edges_to_add: patch.addEdges.map((edge) => ({
        id: edge.id!,
        from_node: edge.fromNodeId,
        to_node: edge.toNodeId,
      })),
      edges_to_delete: patch.deleteEdgeIds,
      properties_to_upsert,
      properties_to_delete: [...new Set(duplicatePropertyIds)],
      // "I meant this." Two legitimate ways to send a bare mass delete: restoring an
      // older version (reverts by deleting everything added since — that is how restore
      // works), or deliberately removing a lot of objects while keeping the rest. Both
      // are declared; a client with an EMPTY graph is neither, and stays refusable —
      // that is the shape an annotator's session sent on dev 2026-08-18 (0 adds / 407
      // deletes, seconds after saving a table).
      allow_bulk_delete: isViewingOlderVersion || clientHasGraphState,
    }

    try {
      await apiService.updateGraph(graphId, graphPatch)

      // Auto-publish HITL corrections to the KG overlay + enqueue a regen
      // (git-model B: human edits auto-approve on save). Awaited so the reload
      // below shows what the approve adds to the graph (links carried onto a
      // drawn symbol). Best-effort: the graph is already persisted, so a failure
      // here must never fail the save UX. Non-P&ID graphs are skipped server-side.
      await apiService.approveHitlCorrections(graphId).catch((err) => {
        console.warn('HITL overlay publish failed (non-fatal):', err)
      })

      // Fetch the latest version history
      const history = await apiService.getVersionHistory(graphId)
      const latestVersion = history[0].version

      // Update version history and current version
      set({
        versionHistory: {
          versions: history,
          currentVersion: latestVersion,
        },
      })

      // Fetch and hydrate state with the latest version data.
      // parseGraph runs on the main thread here, which is acceptable
      // post-save (user is already waiting for the network round-trip).
      const latestGraphData = await apiService.getGraphData(
        graphId,
        { version: latestVersion }
      )
      const { nodeMap, edgeMap } = buildStateMaps(latestGraphData)
      get().hydrateState(
        Array.from(nodeMap.values()),
        Array.from(edgeMap.values()),
        latestGraphData,
        graphId,
      )

      // The stored ground truth follows the HUMAN save. EditorWrapper listens — it
      // owns the rendered page size the GoldDrawing export needs — and freezes the
      // export for this version. Dispatched after hydrate so a listener reading the
      // store sees the saved graph, not the draft.
      dispatchGraphSaved({ graphId, version: latestVersion })
    } catch (error) {
      console.error('Failed to publish graph:', error)
      throw error
    }
  },

  fetchComments: async () =>
    // TODO: Fetch comments from API
    // graph_id: string
    {
      setTimeout(() => {
        set({
          comments: [],
        })
      }, 6000)
    },

  addComment: async (content: string) => {
    const { user, comments } = get()

    const date = new Date().toLocaleDateString()
    const comment: Comment = {
      id: uuidv4(),
      content,
      author: user?.family_name
        ? `${user.given_name} ${user.family_name}`
        : 'Anonymous',
      createdAt: date.toLocaleString(),
      unread: false,
    }
    set({ comments: [...comments, comment] })
  },

  setVersionHistory: (versions) =>
    set({ versionHistory: { ...get().versionHistory, versions } }),

  setCurrentVersion: (version) =>
    set({
      versionHistory: { ...get().versionHistory, currentVersion: version },
    }),

  loadVersion: async (version) => {
    const { graphId } = get()
    const graphData = await apiService.getGraphData(graphId, { version })

    // Only update currentState — keep initialState (and `graph`) as the
    // latest backend state so GraphPatch computes the correct diff.
    // Not setting `graph` avoids stale property IDs in publishGraph's
    // getExistingPropId lookups.
    const { nodeMap, edgeMap } = buildStateMaps(graphData)

    set((state) => ({
      currentState: {
        nodes: nodeMap,
        edges: edgeMap,
        isModified: new GraphPatch(
          state.initialState.nodes,
          state.initialState.edges,
          nodeMap,
          edgeMap,
        ).isModified(),
      },
      versionHistory: { ...state.versionHistory, currentVersion: version },
    }))
  },
})
